import {
    AbacoWebModule,
    RouterReadyEvent,
    VueRouter,
    ApplicationReadyEvent,
    MenuItem,
} from "@abaco/web-common/src/app";
import globals from "./globals";
import myRoutes from "./routes";
import FarmRegistryVuexModule from "./state";
import { HTTPClient } from "@abaco/web-common/src/HTTP";
import { createMenu } from "./menu";
import SSOModule, { MODULE_ID as SSO_MODULE_ID } from "@abaco/sso-web-module/src/abaco-module";
// import GlobalState from "@abaco/portal-global-state/src/modules/global-state/index";
// import CropPlanModule from "@abaco/crop-plan-module/src/modules/crop-plan-module/index";
import SsoVuexModule from "@abaco/sso-web-module/src/abaco-module/state";
import InvolvedApi from "../api/InvolvedApi";
import MapWebModule from "@abaco/map-web-module/src/modules/map-web-module/index";
import SearchFarmApi from "../api/SearchFarmApi";
import FarmRegistryApi from "../api/FarmRegistryApi";
import HolderApi from "../api/HolderApi";
import CampaignApi from "../api/CampaignApi";
import CustomApi from "../api/CustomApi";
import ContractApi from "../api/ContractApi";
import DocumentsApi from "../api/DocumentsApi";

export const MODULE_ID = "farm_registry";

export interface FarmRegistryOptions {
    isReadOnly?: boolean;
    environment?: string;
    skipCheckIsSinglePkcua?: boolean;
}

const state = new FarmRegistryVuexModule();
const entryPoint = "/sitiagri-rest-api/api_basic/v1";

export default class FarmerRegistryModule implements AbacoWebModule {
    public static MODULE_ID = MODULE_ID;

    private router!: VueRouter;
    private http!: HTTPClient;
    private ssoModule!: SSOModule;

    private invApi!: InvolvedApi;
    private srcApi!: SearchFarmApi;
    private frmApi!: FarmRegistryApi;
    private hldrApi!: HolderApi;
    private cmpApi!: CampaignApi;
    private customApi!: CustomApi;
    private conApi!: ContractApi;
    private docApi!: DocumentsApi;
    private apiCreated = false;
    private inflight = false;
    private workInflight = false;
    private organizationInFlight = false;

    readonly isReadOnly: boolean;
    public readonly environment!: string | null;
    readonly skipCheckIsSinglePkcua!: boolean;
    readonly id = MODULE_ID;
    readonly routes = myRoutes;
    readonly globalComponents = globals;
    readonly vuexModule = state;
    menu: MenuItem[] = [];

    constructor(options?: FarmRegistryOptions) {
        this.isReadOnly = options && options.isReadOnly !== undefined ? options.isReadOnly : false;
        this.environment = options?.environment || null;
        this.skipCheckIsSinglePkcua =
            options && options.skipCheckIsSinglePkcua !== undefined ? options.skipCheckIsSinglePkcua : false;
    }

    dependsOn = {
        [MapWebModule.MODULE_ID]: () => new MapWebModule(),
        // [GlobalState.MODULE_ID]: () => new GlobalState(),
    };

    onRouterReady(evt: RouterReadyEvent) {
        this.router = evt.router;
        this.menu = createMenu(this.router);
        this.http = evt.http;
    }

    async beforeEnterAnyRoutes(http: HTTPClient) {
        if (!this.apiCreated) {
            this.apiCreated = true;

            const SearchFarmApi = (await import("../api/SearchFarmApi")).default;
            this.srcApi = new SearchFarmApi(http, this.ssoModule.vuexModule);

            const CampaignApi = (await import("../api/CampaignApi")).default;
            this.cmpApi = new CampaignApi(http);

            const FarmRegistryApi = (await import("../api/FarmRegistryApi")).default;
            this.frmApi = new FarmRegistryApi(http, this.ssoModule.vuexModule,this.isReadOnly);

            const HolderApi = (await import("../api/HolderApi")).default;
            this.hldrApi = new HolderApi(http);

            const InvolvedApi = (await import("../api/InvolvedApi")).default;
            this.invApi = new InvolvedApi(http, entryPoint);

            const CustomApi = (await import("../api/CustomApi")).default;
            this.customApi = new CustomApi(http);

            const ContractApi = (await import("../api/ContractApi")).default;
            this.conApi = new ContractApi(http);

            const DocumentsApi = (await import("../api/DocumentsApi")).default;
            this.docApi = new DocumentsApi(http);
        }
        // the check is needed to force reexecute the auto select after a logout-login and avoid redo it every time it enters in a new route
        if (!this.srcApi || this.inflight || this.vuexModule.autoSelectFarm != null) {
            return;
        }
        this.inflight = true;
        await this.checkIsSinglePkcua();
        this.inflight = false;
    }

    public async checkIsSinglePkcua() {
        const singlePkCuaa = this.skipCheckIsSinglePkcua ? 0 : await this.srcApi.hasOnlyOnefarm();
        if (singlePkCuaa > 0) {
            this.vuexModule.autoSelectableFarm = await this.srcApi.getFarm(singlePkCuaa);
            this.vuexModule.autoSelectFarm = true;
        } else {
            this.vuexModule.autoSelectFarm = false;
            this.vuexModule.autoSelectableFarm = null;
        }
    }

    triggerCampaignReload() {
        this.vuexModule.triggerCampaignReload += 1;
    }

    setCurrentCampaign(campaign: number) {
        this.vuexModule.currentCampaign = campaign;
    }

    async resetfarmIfMissingCompetence() {
        if (!this.vuexModule.farm) {
            return;
        }
        const isUnderCompetence = await this.searchFarmApi.isFarmUnderCompetence(this.vuexModule.farm.pk_cuaa);
        if (!isUnderCompetence) {
            this.vuexModule.farm = null;
        }
    }

    get searchFarmApi() {
        return this.srcApi;
    }

    get campaignApi() {
        return this.cmpApi;
    }

    get farmRegistryApi() {
        return this.frmApi;
    }

    get holderApi() {
        return this.hldrApi;
    }

    get InvolvedApi() {
        return this.invApi;
    }

    get CustomApi() {
        return this.customApi;
    }

    get contractApi() {
        return this.conApi;
    }

    get documentsApi() {
        return this.docApi;
    }

    async onApplicationReady(evt: ApplicationReadyEvent) {
        const ssoModule = evt.modules.find((m) => m.id === SSO_MODULE_ID) as SSOModule | undefined;
        if (!ssoModule) {
            throw new Error("SSOModule not found!");
        }

        this.ssoModule = ssoModule;
    }

    public async updateWorkerCompetences(pkCuaa: number) {
        if (this.workInflight) {
            return;
        }
        this.workInflight = true;
        const url = `/sitiagri-rest-api/api_basic/v1/user_registry/subjects/${pkCuaa}/module_competencies/SOGG_WORKER`;
        const [canView, canEdit] = await Promise.all([this.http.get(`${url}/VIEW`), this.http.get(`${url}/EDIT`)]);
        this.vuexModule.canViewWorker = canView.data.result;
        this.vuexModule.canEditWorker = canEdit.data.result;
        this.workInflight = false;
    }

    public async updateOrganizationCompetences(pkCuaa: number) {
        if (this.organizationInFlight) {
            return;
        }
        this.organizationInFlight = true;

        const res = await this.ssoModule.competenceResources.checkBulkCompetenceForPkcuaa(
            [
                { software: "SOGG_ORG", module: "EDIT" },
                { software: "SOGG_ORG", module: "VIEW" },
                { software: "SOGG_SCS", module: "EDIT" },
                { software: "SOGG_SCS", module: "VIEW" },
                { software: "SOGG_ADMINISTR", module: "EDIT" },
                { software: "SOGG_ADMINISTR", module: "VIEW" },
            ],
            pkCuaa
        );

        // const urlImp = `/sitiagri-rest-api/api_basic/v1/user_registry/subjects/${pkCuaa}/module_competencies/SOGG_WORKER/WORKER_IMPORT`;

        this.vuexModule.canViewOrganization = res["SOGG_ORG|VIEW"];
        this.vuexModule.canEditOrganization = res["SOGG_ORG|EDIT"];
        this.vuexModule.canViewSupplyChain = res["SOGG_SCS|VIEW"];
        this.vuexModule.canEditSupplyChain = res["SOGG_SCS|EDIT"];
        this.vuexModule.canViewAdministrativeRole = res["SOGG_ADMINISTR|VIEW"];
        this.vuexModule.canEditAdministrativeRole = res["SOGG_ADMINISTR|EDIT"];
        this.vuexModule.canImportRegistryBySubject = true;

        this.organizationInFlight = false;
    }
}
